"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.cardSetupDeviceIds = exports.cardSetupBackupId = exports.cardSetupPrimaryId = exports.CARD_SETUP_ISSUE_TEXT = exports.RegisterCardSetupResolvedSchema = exports.RegisterCardSetupResolvedReaderSchema = exports.CARD_SETUP_LANE = exports.CARD_SETUP_CREDIT_METHODS = exports.CARD_SETUP_TRANSPORTS = exports.RegisterCardSetupInputSchema = exports.RegisterCardSetupSchema = exports.RegisterCardSetupReaderSchema = exports.CardSetupRoleSchema = exports.CARD_SETUP_ROLES = exports.REGISTER_CARD_SETUP_PROFILES = void 0;
exports.laneCapabilities = laneCapabilities;
exports.cardSetupIssues = cardSetupIssues;
exports.buildCardSetupResolved = buildCardSetupResolved;
exports.swapCardSetupRoles = swapCardSetupRoles;
exports.cardSetupSmartDefaults = cardSetupSmartDefaults;
const zod_1 = require("zod");
const cardReaders_1 = require("./cardReaders");
/**
 * REGISTER CARD SETUP — a primary + optional backup card reader on ONE register.
 * Spec: docs/agenticpos-rubytill/sunmi-card-setup.md §3 (owner-approved 2026-10-14).
 *
 * ADD-ON ONLY. A register carries `hardwareConfig.cardSetup` only when its
 * catalog model carries a `cardSetupPolicy` (`isCardSetupModel`, today the
 * SUNMI T3 rows); super-admin-ext3 refuses it on every other register
 * (409 CARD_SETUP_T3_ONLY). A register without it behaves exactly as before:
 * the server still writes `hardwareConfig.emvDeviceId` and the register's ONE
 * credit tender = the PRIMARY reader's, so every existing reader of those
 * fields keeps working.
 *
 * RESHAPED 2026-10-14 to `readers[]` (replaces the interim
 * tapToPayEnabled/primary/tapToPayEmvDeviceId/readerEmvDeviceId/debitToBackup
 * shape, which never left dev). Tap to Pay "off" = simply not in `readers`.
 */
exports.REGISTER_CARD_SETUP_PROFILES = ['sunmi_t3'];
exports.CARD_SETUP_ROLES = ['primary', 'backup'];
exports.CardSetupRoleSchema = zod_1.z.enum(exports.CARD_SETUP_ROLES);
exports.RegisterCardSetupReaderSchema = zod_1.z.object({
    role: exports.CardSetupRoleSchema,
    emvDeviceId: zod_1.z.string().min(1).max(128),
});
const setupFields = {
    profile: cardReaders_1.CardSetupProfileSchema,
    /** 1 or 2 entries, exactly one primary. */
    readers: zod_1.z.array(exports.RegisterCardSetupReaderSchema).min(1).max(2),
    /** Show "Use backup reader" on every card payment. */
    showBackupAlways: zod_1.z.boolean().default(true),
    /** Offer the backup when the primary fails — only once the till knows nothing was charged. */
    offerBackupOnFail: zod_1.z.boolean().default(true),
    /** Start on the backup when the primary is offline. */
    startOnBackupWhenPrimaryOffline: zod_1.z.boolean().default(true),
};
/** What is STORED on the register — `tipLocation` always present. */
exports.RegisterCardSetupSchema = zod_1.z.object({
    ...setupFields,
    /** Where the tip is asked: the customer display, the reader taking the card ('terminal'), or not at all. */
    tipLocation: cardReaders_1.TipLocationSchema,
});
/** What a SAVE may send — `tipLocation` absent ⇒ the server's smart default. */
exports.RegisterCardSetupInputSchema = zod_1.z.object({
    ...setupFields,
    tipLocation: cardReaders_1.TipLocationSchema.optional(),
});
/** How the till reaches a reader. Additive enum. */
exports.CARD_SETUP_TRANSPORTS = ['rtpay', 'usb', 'spin_cloud', 'magtek', 'sunbay'];
/** The credit tender each lane records. Additive enum. */
exports.CARD_SETUP_CREDIT_METHODS = ['credit_dejavoo_rtpay', 'credit_dejavoo', 'credit_dejavoo_cloud', 'credit_magtek', 'credit_sunbay'];
/** deviceClass → how the till reaches it and the tender it records. Unknown class ⇒ undefined. */
exports.CARD_SETUP_LANE = {
    dejavoo_rubytill_pay: { transport: 'rtpay', creditMethod: 'credit_dejavoo_rtpay', emvMode: 'rtpay' },
    dejavoo_spin_usb: { transport: 'usb', creditMethod: 'credit_dejavoo', emvMode: 'usb' },
    dejavoo_spin_cloud: { transport: 'spin_cloud', creditMethod: 'credit_dejavoo_cloud', emvMode: 'spin_cloud' },
    /* MagTek DynaFlex II over USB → pos-magtek-service (UniGate). The lane runs
       every sale as CREDIT (UniGate paymentType 'Credit'), asks no tip and takes
       no signature on the reader — so a PED's PIN pad is not a debit route yet. */
    magtek_usb: {
        transport: 'magtek', creditMethod: 'credit_magtek', emvMode: 'magtek',
        laneLimits: { pinDebit: false, tipOnScreen: false, signatureOnScreen: false },
    },
    /* SUNMI CPad Pay running SUNBAY Tapro → pos-sunbay-service. Tapro is only a
       terminal: RubyTill owns tip, signature and receipt, so none run on it. */
    sunbay_tapro: {
        transport: 'sunbay', creditMethod: 'credit_sunbay', emvMode: 'sunbay',
        laneLimits: { pinDebit: false, tipOnScreen: false, signatureOnScreen: false },
    },
};
/** Device capabilities narrowed to what the device's lane can run. */
function laneCapabilities(deviceClass, caps) {
    const limits = exports.CARD_SETUP_LANE[deviceClass]?.laneLimits;
    if (!limits)
        return caps;
    const out = { ...caps };
    for (const k of Object.keys(limits))
        if (limits[k] === false)
            out[k] = false;
    return out;
}
/** One reader as the till reaches it — written by the server at save time. */
exports.RegisterCardSetupResolvedReaderSchema = zod_1.z.object({
    role: exports.CardSetupRoleSchema,
    emvDeviceId: zod_1.z.string().min(1),
    kind: cardReaders_1.ReaderKindSchema,
    deviceClass: zod_1.z.string().min(1),
    transport: zod_1.z.enum(exports.CARD_SETUP_TRANSPORTS),
    creditMethod: zod_1.z.enum(exports.CARD_SETUP_CREDIT_METHODS),
    deviceName: zod_1.z.string().nullable(),
    /** Processor-neutral name for merchant/cashier text ("Tap to Pay on this till", …). */
    neutralName: zod_1.z.string(),
    capabilities: cardReaders_1.ReaderCapabilitiesSchema,
});
exports.RegisterCardSetupResolvedSchema = zod_1.z.object({
    /** Primary first. */
    readers: zod_1.z.array(exports.RegisterCardSetupResolvedReaderSchema).min(1).max(2),
    /** From the model's policy. */
    customerDisplay: zod_1.z.boolean(),
    /** First reader (primary first) with pinDebit — where a debit choice goes. Null = no debit. */
    debitReaderId: zod_1.z.string().nullable(),
    /** ISO timestamp. */
    resolvedAt: zod_1.z.string(),
});
exports.CARD_SETUP_ISSUE_TEXT = {
    NO_PRIMARY: 'Pick the primary card reader.',
    TWO_PRIMARIES: 'Only one reader can be the primary.',
    TWO_BACKUPS: 'Only one reader can be the backup.',
    SAME_DEVICE_TWICE: 'The primary and the backup must be two different readers.',
    PROFILE_MISMATCH: 'This card setup is for a different kind of till.',
    NOT_A_CARD_READER: 'That device is not a card reader this till can use.',
    READER_NOT_ALLOWED_AS_PRIMARY: 'That reader cannot be the primary on this till.',
    READER_NOT_ALLOWED_AS_BACKUP: 'That reader cannot be the backup on this till.',
    BACKUP_REQUIRED: 'This till needs a backup reader.',
    TIP_NO_CUSTOMER_DISPLAY: 'This till has no customer display to ask for the tip.',
    TIP_NO_TERMINAL_SCREEN: 'No card reader on this till can ask for a tip.',
};
/**
 * The rules, as data — the server refuses on any, the UI disables Save on any.
 *   readers allowed by the model's policy for their role (and enabled);
 *   exactly one primary, ≤1 backup, distinct devices; backup required unless allowNoBackup;
 *   tipLocation 'customer_display' needs policy.customerDisplay,
 *   'terminal' needs some reader with tipOnScreen.
 * `info` is optional per device: absent ⇒ only the structural checks for it.
 */
function cardSetupIssues(s, policy, info = {}) {
    const out = [];
    const readers = Array.isArray(s.readers) ? s.readers : [];
    if (s.profile && s.profile !== policy.profile)
        out.push({ code: 'PROFILE_MISMATCH' });
    const primaries = readers.filter((r) => r.role === 'primary');
    const backups = readers.filter((r) => r.role === 'backup');
    if (primaries.length === 0)
        out.push({ code: 'NO_PRIMARY' });
    if (primaries.length > 1)
        out.push({ code: 'TWO_PRIMARIES' });
    if (backups.length > 1)
        out.push({ code: 'TWO_BACKUPS' });
    if (new Set(readers.map((r) => r.emvDeviceId)).size !== readers.length)
        out.push({ code: 'SAME_DEVICE_TWICE' });
    if (backups.length === 0 && !policy.allowNoBackup)
        out.push({ code: 'BACKUP_REQUIRED' });
    for (const r of readers) {
        const i = info[r.emvDeviceId];
        if (!i)
            continue;
        if (!i.kind) {
            out.push({ code: 'NOT_A_CARD_READER', emvDeviceId: r.emvDeviceId, role: r.role });
            continue;
        }
        if (!(0, cardReaders_1.policyAllows)(policy, i.kind, r.role)) {
            out.push({ code: r.role === 'primary' ? 'READER_NOT_ALLOWED_AS_PRIMARY' : 'READER_NOT_ALLOWED_AS_BACKUP', emvDeviceId: r.emvDeviceId, role: r.role });
        }
    }
    if (s.tipLocation && s.tipLocation !== 'off') {
        const lockReaders = readers.map((r) => ({ emvDeviceId: r.emvDeviceId, role: r.role, capabilities: info[r.emvDeviceId]?.capabilities ?? null }));
        const known = readers.every((r) => !!info[r.emvDeviceId]);
        if (s.tipLocation === 'customer_display' && !policy.customerDisplay)
            out.push({ code: 'TIP_NO_CUSTOMER_DISPLAY' });
        if (s.tipLocation === 'terminal' && known && !(0, cardReaders_1.tipLocationAllowed)({ readers: lockReaders, customerDisplay: policy.customerDisplay }, 'terminal')) {
            out.push({ code: 'TIP_NO_TERMINAL_SCREEN' });
        }
    }
    return out;
}
const cardSetupPrimaryId = (s) => s?.readers?.find((r) => r.role === 'primary')?.emvDeviceId ?? null;
exports.cardSetupPrimaryId = cardSetupPrimaryId;
const cardSetupBackupId = (s) => s?.readers?.find((r) => r.role === 'backup')?.emvDeviceId ?? null;
exports.cardSetupBackupId = cardSetupBackupId;
/** Every device id a setup names, primary first. */
const cardSetupDeviceIds = (s) => [(0, exports.cardSetupPrimaryId)(s), (0, exports.cardSetupBackupId)(s)].filter((x) => !!x);
exports.cardSetupDeviceIds = cardSetupDeviceIds;
/**
 * Build `cardSetupResolved` from the device rows (and each device's catalog
 * row, when the caller has one). Pure. `{ error }` when a device is missing
 * or its class has no lane / no kind.
 */
function buildCardSetupResolved(s, devices, policy, opts = {}) {
    const ordered = [...s.readers].sort((a, b) => (a.role === 'primary' ? 0 : 1) - (b.role === 'primary' ? 0 : 1));
    const readers = [];
    for (const r of ordered) {
        const dev = devices[r.emvDeviceId];
        if (!dev)
            return { error: 'DEVICE_MISSING', emvDeviceId: r.emvDeviceId };
        const deviceClass = String(dev.deviceClass || '');
        const lane = exports.CARD_SETUP_LANE[deviceClass];
        const kind = (0, cardReaders_1.readerKindOf)(dev);
        const deviceCaps = (0, cardReaders_1.capabilitiesOf)(dev, opts.catalogByDeviceId?.[r.emvDeviceId] ?? null);
        if (!lane || !kind || !deviceCaps)
            return { error: 'UNSUPPORTED_DEVICE', emvDeviceId: r.emvDeviceId };
        const capabilities = laneCapabilities(deviceClass, deviceCaps);
        readers.push({
            role: r.role, emvDeviceId: r.emvDeviceId, kind, deviceClass,
            transport: lane.transport, creditMethod: lane.creditMethod,
            deviceName: dev.deviceName || null,
            neutralName: (0, cardReaders_1.readerNeutralName)(kind),
            capabilities,
        });
    }
    return {
        resolved: {
            readers,
            customerDisplay: !!policy.customerDisplay,
            debitReaderId: (0, cardReaders_1.debitReaderIdOf)(readers),
            resolvedAt: (opts.now ?? new Date()).toISOString(),
        },
    };
}
/**
 * The merchant's ONE edit: make `newPrimaryId` (today's backup) the primary.
 * Null when it is not the backup of this setup. Already primary ⇒ unchanged copy.
 */
function swapCardSetupRoles(s, newPrimaryId) {
    if ((0, exports.cardSetupPrimaryId)(s) === newPrimaryId)
        return { ...s, readers: s.readers.map((r) => ({ ...r })) };
    if ((0, exports.cardSetupBackupId)(s) !== newPrimaryId)
        return null;
    return {
        ...s,
        readers: s.readers
            .map((r) => ({ ...r, role: (r.emvDeviceId === newPrimaryId ? 'primary' : 'backup') }))
            .sort((a, b) => (a.role === 'primary' ? 0 : 1) - (b.role === 'primary' ? 0 : 1)),
    };
}
/**
 * SMART DEFAULTS on a register's FIRST card setup (spec §3):
 *   credit on (the save path assigns + enables the primary's tender);
 *   debit on ⇔ a PIN reader exists;
 *   tips at the customer display if present, else the terminal (else off);
 *   signature / surcharge / split = the existing defaults (untouched).
 */
function cardSetupSmartDefaults(resolved) {
    return {
        tipLocation: (0, cardReaders_1.defaultTipLocation)(resolved),
        debitEnabled: !!resolved.debitReaderId,
    };
}
//# sourceMappingURL=registerCardSetup.js.map